import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Calendar, CheckSquare } from "lucide-react";
import { cn } from "@/lib/utils";

const STATUS_CONFIG = {
  "Planning": { bg: "bg-purple-100", text: "text-purple-700" },
  "Active": { bg: "bg-green-100", text: "text-green-700" },
  "On Hold": { bg: "bg-amber-100", text: "text-amber-700" },
  "In Review": { bg: "bg-blue-100", text: "text-blue-700" },
  "Completed": { bg: "bg-teal-100", text: "text-teal-700" },
  "Archived": { bg: "bg-gray-100", text: "text-gray-700" }
};

const PRIORITY_CONFIG = {
  "High": { bg: "bg-red-100", text: "text-red-700" },
  "Medium": { bg: "bg-amber-100", text: "text-amber-700" },
  "Low": { bg: "bg-green-100", text: "text-green-700" }
};

const ProjectListRow = ({ project, onEdit, onDelete, onDuplicate, onArchive }) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);

  const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG["Planning"];
  const priorityConfig = PRIORITY_CONFIG[project.priority] || PRIORITY_CONFIG["Medium"];
  
  const isOverdue = project.dueDate && new Date(project.dueDate) < new Date() && project.status !== "Completed";
  
  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const handleRowClick = (e) => {
    if (e.target.closest('button') || e.target.closest('[data-menu]')) {
      return;
    }
    navigate(`/projects/${project.id}`);
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (index) => {
    const colors = [
      "from-blue-500 to-blue-600",
      "from-purple-500 to-purple-600",
      "from-pink-500 to-pink-600",
      "from-green-500 to-green-600",
      "from-amber-500 to-amber-600",
      "from-red-500 to-red-600"
    ];
    return colors[index % colors.length];
  };

  const assignedMembers = project.assignedMembers || [];
  const displayedMembers = assignedMembers.slice(0, 3);
  const remainingCount = assignedMembers.length - 3;

  const menuItems = [
    { label: "View Details", action: () => navigate(`/projects/${project.id}`) },
    { label: "Edit Project", action: () => onEdit && onEdit(project) },
    { label: "Duplicate", action: () => onDuplicate && onDuplicate(project) },
    { label: "Archive", action: () => onArchive && onArchive(project) },
    { label: "Delete", action: () => onDelete && onDelete(project), danger: true }
  ];

  return (
    <Card 
      className="mb-2 hover:bg-slate-50 transition-colors cursor-pointer"
      onClick={handleRowClick}
    >
      <CardContent className="py-3 px-4 flex items-center gap-4">
        <div 
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: project.color || "#6366F1" }}
        />
        
        <div className="min-w-[200px] flex-1">
          <span className="font-semibold text-navy">{project.name}</span>
        </div>
        
        <div className="min-w-[100px]">
          <Badge className={cn(statusConfig.bg, statusConfig.text)}>
            {project.status}
          </Badge>
        </div>
        
        <div className="min-w-[80px]">
          <span className={cn("text-sm", priorityConfig.text)}>
            {project.priority || "Medium"}
          </span>
        </div>
        
        <div className="min-w-[120px] flex items-center gap-2">
          <Progress value={project.progress || 0} className="flex-1 h-2" />
          <span className="text-xs text-gray-500 whitespace-nowrap">{project.progress || 0}%</span>
        </div>
        
        <div className="min-w-[100px] flex -space-x-2">
          {displayedMembers.map((memberId, index) => (
            <div
              key={memberId}
              className={cn(
                "w-7 h-7 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-xs font-medium border-2 border-white",
                getAvatarColor(index)
              )}
            >
              {getInitials(`User ${index + 1}`)}
            </div>
          ))}
          {remainingCount > 0 && (
            <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-xs font-medium border-2 border-white">
              +{remainingCount}
            </div>
          )}
        </div>
        
        <div className={cn("min-w-[100px] text-sm", isOverdue && "text-red-600 font-medium")}>
          {formatDate(project.dueDate)}
        </div>
        
        <div className="min-w-[80px] text-sm text-gray-600 flex items-center gap-1">
          <CheckSquare className="w-4 h-4" />
          {project.tasksCount || 0}/{project.totalTasks || 20}
        </div>
        
        <div className="min-w-[40px] flex justify-end" data-menu>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
          >
            <MoreHorizontal className="w-4 h-4" />
          </Button>
          {showMenu && (
            <div className="absolute right-4 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20">
              {menuItems.map((item, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    item.action();
                    setShowMenu(false);
                  }}
                  className={cn(
                    "w-full px-4 py-2 text-sm text-left hover:bg-slate-50",
                    item.danger ? "text-red-600" : "text-navy"
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectListRow;